import Card from "@/components/card"
import { t } from "i18next"
import { faBuilding, faPen, faTrash, faRightToBracket } from "@fortawesome/free-solid-svg-icons"
import Space from "@/components/space"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "../model/establishment.store"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Link from "@/components/link"
import Chip from "@/components/chip"
import { Establishment } from "../model/establishment.types"
import { User } from "@/redux/user.types"
import { useState } from "react"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"
import { useNavigate } from "react-router-dom"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import LoadingModal from "@/modals/loading.modal"

const MODAL_LOADING = "MODAL_LOADING"
const MODAL_SEND_EMAIL = "MODAL_SEND_EMAIL"
const MODAL_USER_EDIT = "MODAL_USER_EDIT"

interface Props {
  children?: React.ReactNode //Sous la fiche, dans la meme carte (ex : contributeurs en multi-etablissements)
  onDelete?: (open: boolean) => void
  onEdit: (establishment: Establishment) => void
  showConnectButton?: boolean
  establishment: Establishment
}

export default function EstablishmentCard({ children, onDelete, onEdit, showConnectButton, establishment }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const establishments = useAppSelector(establishmentStore.selectors.list)

  const [modal, setModal] = useState<null | string>(null)
  const [currentUser, setCurrentUser] = useState(new User())

  const handleDelete = (id: string) => {
    if (onDelete) {
      const establishment = establishments.find((e) => e.id === id)
      dispatch(establishmentStore.actions.setActive(establishment))
      if (!establishment) return
      dispatch(establishmentStore.actions.select(establishment.id))
      onDelete(true)
    }
  }

  //Connec to the establishment
  //Save new jwt (containing establishment id)
  async function enterEstablishmentContext(establishment: Establishment) {
    setModal(MODAL_LOADING)
    const response = await dispatch(establishmentStore.thunks.enterContext(establishment)).unwrap()
    setModal(null)
    if (!response.error) {
      navigate("/home")
    }
  }

  function InfoBlock({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <div className="flex flex-col">
        <div className="text-input-title flex">
          <span className="grey-t">{label}</span>
        </div>
        <div>{children}</div>
      </div>
    )
  }

  return (
    <>
      {modal === MODAL_LOADING && <LoadingModal />}

      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onDelete={() => setModal(null)}
          onNext={(user) => {
            setCurrentUser(user)
            setModal(null)
          }}
        />
      )}

      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onDelete={() => setModal(null)}
          onNext={(user) => {
            setCurrentUser(user)
            setModal(null)
          }}
        />
      )}

      {modal === MODAL_SEND_EMAIL && (
        <AdmSendEmailModal
          emailCode="inviteUnitContributor"
          currentUser={currentUser}
          onClose={() => setModal(null)}
          textBold={`Souhaitez-vous envoyer un email d'invitation à ${currentUser.username} (${currentUser.email}) en tant que contributeur du document unique pour l'unité de travail ${establishment.name} ?`}
          items={[{ id: "unit", value: establishment.name }]}
        />
      )}

      <Card>
        <div className="mb-4 flex items-center gap-3">
          <FontAwesomeIcon icon={faBuilding} className="grey-t text-lg" />
          <span className="text-lg">{establishment.name}</span>
          <Space />

          {onDelete && (
            <Link icon={faTrash} isWithoutMargin onClick={() => handleDelete(establishment.id)}>
              {t("utils_delete")}
            </Link>
          )}

          <Link icon={faPen} isWithoutMargin onClick={() => onEdit(establishment)}>
            {t("utils_edit")}
          </Link>

          {showConnectButton && (
            <Link icon={faRightToBracket} isWithoutMargin onClick={() => enterEstablishmentContext(establishment)}>
              Se connecter
            </Link>
          )}
        </div>
        <div className="flex gap-12">
          {establishment.isDisabled && (
            <InfoBlock label={t("Statut")}>
              <Chip>Inactif</Chip>
            </InfoBlock>
          )}
          <InfoBlock label={t("N° SIRET")}>
            {establishment.codeEstablishment ? establishment.codeEstablishment : t("Non renseigné")}
          </InfoBlock>
          <InfoBlock label={t("Code NAF")}>
            {establishment.industryCode ? establishment.industryCode : t("Non renseigné")}
          </InfoBlock>
          <InfoBlock label={t("Adresse")}>
            {establishment.address ? establishment.address : t("Non renseigné")}
          </InfoBlock>
        </div>

        {children && <div className="mt-10">{children}</div>}
      </Card>
    </>
  )
}
